import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { validateMobile, saveTrackedOrder, trackUrl } from "@/lib/orderTracking";

export function CartDrawer({ open, onClose, menu, tableId }) {
  const { items, updateQty, removeItem, clear, estimate } = useCart();
  const [cust, setCust] = useState({ customer_name: "", customer_phone: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  const [serverPhoneError, setServerPhoneError] = useState("");
  const phoneError = validateMobile(cust.customer_phone) || serverPhoneError;
  const navigate = useNavigate();
  const gst = menu.restaurant.gst || {};

  const place = async () => {
    setTouched(true);
    if (phoneError) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/public/menu/${menu.restaurant.slug}/orders`, {
        items: items.map(({ product_id, quantity, variation_selections, addon_selections, size_id }) => ({ product_id, quantity, variation_selections, addon_selections, size_id: size_id || "" })),
        table_id: tableId, ...cust,
      });
      clear();
      toast.dismiss();
      const tracked = { id: data.order.id, token: data.token, number: data.order.order_number, slug: menu.restaurant.slug, at: new Date().toISOString() };
      saveTrackedOrder(tracked);
      navigate(trackUrl(tracked));
    } catch (e) {
      const msg = apiError(e, "Could not place order");
      if (/mobile/i.test(msg)) { setServerPhoneError(msg); setTouched(true); } else toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="mx-auto max-h-[90vh] max-w-3xl overflow-y-auto rounded-t-3xl" data-testid="cart-drawer">
        <SheetHeader><SheetTitle className="font-heading text-xl">Your order {menu.table && `· ${menu.table.name}`}</SheetTitle></SheetHeader>
        {items.length === 0 ? <p className="py-10 text-center text-sm text-slate-400">Your cart is empty.</p> : (
          <div className="mt-4 space-y-5">
            <div className="divide-y divide-slate-100">
              {items.map((i) => (
                <div key={i.uid} className="flex items-center gap-3 py-3" data-testid="cart-line">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-800">{i.name}</p>
                    {i.labels?.length > 0 && <p className="truncate text-xs text-slate-400">{i.labels.join(", ")}</p>}
                    <p className="text-sm text-slate-600">{money(i.unit_price * i.quantity)}</p>
                  </div>
                  <div className="flex items-center rounded-full border border-slate-200">
                    <button onClick={() => (i.quantity === 1 ? removeItem(i.uid) : updateQty(i.uid, i.quantity - 1))} className="p-2" data-testid="cart-qty-minus">{i.quantity === 1 ? <Trash2 className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}</button>
                    <span className="w-5 text-center text-sm font-semibold">{i.quantity}</span>
                    <button onClick={() => updateQty(i.uid, Math.min(50, i.quantity + 1))} className="p-2" data-testid="cart-qty-plus"><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="checkout-phone" className="mb-1.5 block text-sm font-medium text-slate-700">Mobile Number <span className="text-rose-500">*</span></label>
                <Input id="checkout-phone" type="tel" inputMode="tel" autoComplete="tel" required placeholder="98765 43210"
                  value={cust.customer_phone} onChange={(e) => { setServerPhoneError(""); setCust({ ...cust, customer_phone: e.target.value }); }} onBlur={() => setTouched(true)}
                  aria-invalid={touched && !!phoneError} aria-describedby="checkout-phone-error"
                  className={touched && phoneError ? "border-rose-400 focus-visible:ring-rose-300" : ""} data-testid="checkout-phone" />
                {touched && phoneError ? (
                  <p id="checkout-phone-error" className="mt-1 text-xs text-rose-600" data-testid="checkout-phone-error">{phoneError}</p>
                ) : <p className="mt-1 text-xs text-slate-400">For order updates. Use +country code if outside India.</p>}
              </div>
              <div>
                <label htmlFor="checkout-name" className="mb-1.5 block text-sm font-medium text-slate-700">Your name (optional)</label>
                <Input id="checkout-name" placeholder="Name" value={cust.customer_name} onChange={(e) => setCust({ ...cust, customer_name: e.target.value })} data-testid="checkout-name" />
              </div>
            </div>
            <Textarea rows={2} placeholder="Note for the kitchen (optional)" value={cust.note} onChange={(e) => setCust({ ...cust, note: e.target.value })} data-testid="checkout-note" />
            <div className="rounded-2xl bg-slate-50 p-4 text-sm">
              <div className="flex justify-between"><span>Items total</span><span data-testid="cart-estimate">{money(estimate)}</span></div>
              {gst.enabled && <p className="mt-1 text-xs text-slate-400">{gst.inclusive ? `Prices include GST.` : `GST will be added at checkout.`} Final amount is confirmed by the restaurant's system.</p>}
            </div>
            <Button onClick={place} disabled={busy || (touched && !!phoneError)} className="h-12 w-full rounded-full bg-emerald-500 text-base hover:bg-emerald-600" data-testid="place-order-btn">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Place order
            </Button>
            <p className="text-center text-xs text-slate-400">Pay at the counter after your meal.</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
